import { useEffect, useState } from "react";
import { Navigate, Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import InternalAIChatWidget from "@/components/app/InternalAIChatWidget";
import { canUseMarketing } from "@/lib/permissions";
import { PendingNotesModal } from "@/components/app/PendingNotesModal";
import {
  LayoutDashboard,
  FilePlus2,
  Users,
  LogOut,
  ScrollText,
  CarTaxiFront,
  ClipboardList,
  Megaphone,
  Menu,
  X,
  MessageSquare,
  Archive,
  Home,
  Bell,
  Euro,
  Languages,
  KeyRound,
  StickyNote,
  CalendarDays,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_complaint-tracker-102/artifacts/5d2kmds0_IMG_2B71FDB9-74FD-4451-B622-B7FCD5B5DCBD.jpeg";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);

  useEffect(() => {
    if (!user || user === false || user === null) return;
    let active = true;
    const fetchUnread = () => {
      api
        .get("/announcements/unread-count")
        .then((r) => active && setUnread(r.data.unread || 0))
        .catch(() => {});
      api
        .get("/chat/unread-count")
        .then((r) => active && setChatUnread(r.data.unread || 0))
        .catch(() => {});
      api
        .get("/notifications/unread-count")
        .then((r) => active && setNotifUnread(r.data.unread || 0))
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [user, location.pathname]);

  if (user === null) {
    return (
      <div className="h-screen w-screen flex items-center justify-center text-sm text-zinc-500">
        Cargando…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const navItem =
    "flex items-center gap-3 px-5 py-3 text-sm font-medium tracking-wide transition-colors";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isDriver = user.role === "driver";
  const isAdmin = user.role === "admin";

  // Close mobile drawer whenever the route changes
  const closeMobile = () => setMobileOpen(false);

  const SidebarBody = (
    <>
      <div className="px-5 py-6 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="DS Travel Tours"
            className="w-12 h-12 object-cover rounded-sm"
          />
          <div>
            <div className="font-display text-white text-base leading-tight font-bold">
              DS Travel Tours
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Centro de Control
            </div>
          </div>
        </div>
        <button
          onClick={closeMobile}
          className="md:hidden text-white"
          aria-label="Cerrar menú"
          data-testid="sidebar-close"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 py-4 flex flex-col overflow-y-auto">
        <NavLink
          to="/"
          end
          data-testid="nav-home"
          onClick={closeMobile}
          className={({ isActive }) => `${navItem} ${isActive ? "active" : ""} justify-between`}
        >
          <span className="flex items-center gap-3">
            <Home size={18} /> Inicio
          </span>
          {notifUnread > 0 && (
            <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
              {notifUnread}
            </span>
          )}
        </NavLink>
        {isDriver ? (
          <>
            <NavLink
              to="/driver"
              data-testid="nav-driver"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <CarTaxiFront size={18} /> Mis servicios
            </NavLink>
            <NavLink
              to="/announcements"
              data-testid="nav-announcements-driver"
              onClick={closeMobile}
              className={({ isActive }) =>
                `${navItem} ${isActive ? "active" : ""} justify-between`
              }
            >
              <span className="flex items-center gap-3">
                <Megaphone size={18} /> Comunicados
              </span>
              {unread > 0 && (
                <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                  {unread}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/chat"
              data-testid="nav-chat-driver"
              onClick={closeMobile}
              className={({ isActive }) =>
                `${navItem} ${isActive ? "active" : ""} justify-between`
              }
            >
              <span className="flex items-center gap-3">
                <MessageSquare size={18} /> Chat con supervisor
              </span>
              {chatUnread > 0 && (
                <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                  {chatUnread}
                </span>
              )}
            </NavLink>
          </>
        ) : (
          <>
            <div className="px-5 pt-2 pb-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Servicios
            </div>
            <NavLink
              to="/services"
              data-testid="nav-services"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <ClipboardList size={18} /> Reservas
            </NavLink>
            <NavLink
              to="/services/new"
              data-testid="nav-new-service"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <FilePlus2 size={18} /> Nueva reserva
            </NavLink>
            <NavLink
              to="/services/historial"
              data-testid="nav-services-history"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <Archive size={18} /> Historial
            </NavLink>

            <div className="px-5 pt-5 pb-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Reclamaciones
            </div>
            <NavLink
              to="/dashboard"
              data-testid="nav-dashboard"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <LayoutDashboard size={18} /> Panel
            </NavLink>
            <NavLink
              to="/complaints/new"
              data-testid="nav-new-complaint"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <ScrollText size={18} /> Nueva reclamación
            </NavLink>

            <div className="px-5 pt-5 pb-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Comunicación
            </div>
            <NavLink
              to="/announcements"
              data-testid="nav-announcements-staff"
              onClick={closeMobile}
              className={({ isActive }) =>
                `${navItem} ${isActive ? "active" : ""} justify-between`
              }
            >
              <span className="flex items-center gap-3">
                <Megaphone size={18} /> Comunicados
              </span>
              {unread > 0 && (
                <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                  {unread}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/chat"
              data-testid="nav-chat-staff"
              onClick={closeMobile}
              className={({ isActive }) =>
                `${navItem} ${isActive ? "active" : ""} justify-between`
              }
            >
              <span className="flex items-center gap-3">
                <MessageSquare size={18} /> Chat conductores
              </span>
              {chatUnread > 0 && (
                <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                  {chatUnread}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/traductor"
              data-testid="nav-translator"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <Languages size={18} /> Traductor Universal
            </NavLink>
            <NavLink
              to="/notas"
              data-testid="nav-notes"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <StickyNote size={18} /> Notas
            </NavLink>
            <NavLink
              to="/calendario"
              data-testid="nav-calendar"
              onClick={closeMobile}
              className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
            >
              <CalendarDays size={18} /> Calendario
            </NavLink>
            {canUseMarketing(user) && (
              <NavLink
                to="/marketing"
                data-testid="nav-marketing"
                onClick={closeMobile}
                className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
              >
                <Megaphone size={18} /> Marketing
              </NavLink>
            )}

            {isAdmin && (
              <>
                <div className="px-5 pt-5 pb-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Administración
                </div>
                <NavLink
                  to="/users"
                  data-testid="nav-users"
                  onClick={closeMobile}
                  className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
                >
                  <Users size={18} /> Usuarios y conductores
                </NavLink>
                <NavLink
                  to="/precios"
                  data-testid="nav-pricing"
                  onClick={closeMobile}
                  className={({ isActive }) => `${navItem} ${isActive ? "active" : ""}`}
                >
                  <Euro size={18} /> Precios globales
                </NavLink>
              </>
            )}
          </>
        )}
      </nav>

      <div className="p-5 border-t border-white/10">
        <div className="text-xs text-zinc-400 uppercase tracking-widest mb-1">
          Sesión · {user.role}
        </div>
        <div className="text-sm text-white font-semibold truncate" data-testid="current-user-name">
          {user.name}
        </div>
        {!isDriver && (
          <div className="text-xs text-zinc-500 truncate mb-3">{user.email}</div>
        )}
        {isDriver && <div className="mb-3" />}
        <NavLink
          to="/perfil"
          onClick={closeMobile}
          data-testid="nav-profile"
          className="w-full mb-2 flex items-center justify-center gap-2 py-2 px-3 border border-white/30 text-white text-xs font-semibold uppercase tracking-widest hover:bg-white/10 transition-colors"
        >
          <KeyRound size={14} /> Mi perfil
        </NavLink>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-white text-black text-xs font-semibold uppercase tracking-widest hover:bg-zinc-200 transition-colors"
        >
          <LogOut size={14} /> Salir
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen md:flex bg-white">
      {/* Mobile top bar */}
      {/* Floating bell (desktop) */}
      {user && (
        <NavLink
          to="/"
          end
          className="hidden md:flex fixed top-4 right-6 z-40 items-center gap-2 h-10 px-3 border border-black bg-white text-black hover:bg-black hover:text-white transition-colors"
          data-testid="floating-notif-btn"
          onClick={closeMobile}
        >
          <Bell size={16} />
          {notifUnread > 0 && (
            <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm min-w-[18px] text-center">
              {notifUnread}
            </span>
          )}
        </NavLink>
      )}

      <header
        className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-black text-white border-b border-zinc-800"
        data-testid="mobile-top-bar"
      >
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="DS Travel Tours" className="w-9 h-9 object-cover" />
          <div className="leading-tight">
            <div className="font-display text-sm font-bold">DS Travel Tours</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-400">
              Centro de Control
            </div>
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2"
          aria-label="Abrir menú"
          data-testid="mobile-menu-toggle"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Desktop sidebar */}
      <aside
        className="ds-sidebar w-64 shrink-0 flex-col sticky top-0 h-screen hidden md:flex"
        data-testid="app-sidebar"
      >
        {SidebarBody}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            onClick={closeMobile}
            data-testid="mobile-drawer-backdrop"
          />
          <aside
            className="ds-sidebar fixed inset-y-0 left-0 z-50 w-72 flex flex-col md:hidden animate-fade-up"
            data-testid="mobile-drawer"
          >
            {SidebarBody}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <InternalAIChatWidget />
      <PendingNotesModal />
    </div>
  );
}
